import React, { Component } from "react";
import { map, get } from "lodash";

import PropTypes from "prop-types";
import equal from "deep-equal";
import TranscriptView from "./TranscriptView/TranscriptView";
import transcriptCompiler from "../../../utilities/transcriptLib/transcriptCompiler";
import sentencesBySpeaker from "../../../utilities/transcriptLib/transcriptTransformers/sentencesBySpeaker";
import markIdentifiedRanges from "../../../utilities/transcriptLib/transcriptTransformers/markIdentifiedRanges";
import Range from "../../../utilities/transcriptLib/Range";
import Term from "../../../utilities/transcriptLib/Term";
import {
  DEFAULT_SPEAKER,
  SPEAKER_COLORS,
} from "../../../utilities/transcriptLib/constants";
import compareRangeLists from "../../../utilities/transcriptLib/compareRangeLists";
import chunkSpeakers from "../../../utilities/transcriptLib/transcriptTransformers/chunkSpeakers";
import ScrollerContext from "../../ScrollerContext";

const SINGLE_SPEAKER_COLOR = "black";
const chunkSize = 12000;

export const TranscriptContext = React.createContext({
  transcript: [],
  transcriptBySpeaker: [],
  speakers: [],
  transcriptTimeRanges: [],
  identifiedRanges: [],
  identifiedRangeBrackets: [],
});

class TranscriptContainer extends Component {
  constructor(props) {
    super(props);
    this.state = this.propsToState();
  }

  identifiedRangeBrackets(pProps) {
    const props = pProps || this.props;
    if (props.markedRanges.length) return props.markedRanges;
    return [];
  }

  getSpeakerColorsFromSpeakers(speakers) {
    const map = new Map([[DEFAULT_SPEAKER, SINGLE_SPEAKER_COLOR]]);
    if (!speakers || speakers.size < 2) return map;
    let index = 0;
    speakers.forEach((name) => {
      map.set(name, SPEAKER_COLORS[index]);
      index += 1;
    });
    return map;
  }

  transcriptBySpeaker(pProps) {
    const props = pProps || this.props;
    const terms = transcriptCompiler(props.transcript);
    const bySpeaker = sentencesBySpeaker(terms);
    bySpeaker.value = chunkSpeakers(bySpeaker.value, chunkSize, props.fullTurns);
    const brackets = this.identifiedRangeBrackets(props);
    if (brackets) {
      markIdentifiedRanges(bySpeaker, brackets);
    }
    return bySpeaker;
  }

  transcriptTimeRanges(pProps) {
    const props = pProps || this.props;
    if (!props.transcript) return [];
    return map(
      props.transcript.filter((t) => t && t.s && t.e && !t.m),
      (t) =>
        new Range({
          name: t.p,
          start: t.s,
          end: t.e,
        })
    );
  }

  getSpeakersFromTBS(transcriptBySpeaker) {
    if (transcriptBySpeaker && transcriptBySpeaker instanceof Term) {
      const speakers = transcriptBySpeaker.get("speakers");
      if (speakers && speakers instanceof Set)
        return Array.from(speakers.values());
    }
    return [DEFAULT_SPEAKER];
  }

  shouldComponentUpdate(newProps, newState) {
    if (this.state.scrollHeight !== newState.scrollHeight) return true;
    if (newProps.transcript !== this.props.transcript) return true;
    if (
      !compareRangeLists(
        newState.transcriptTimeRanges,
        this.state.transcriptTimeRanges
      )
    )
      return true;

    if (!equal(newProps.query, this.props.query)) return true;
    return !equal(newProps.markedRanges, this.props.markedRanges);
  }

  getScrollerContextValue() {
    const height = this.state.scrollHeight;
    return {
      scrollRef: this.state.scrollRef,
      setScrollRef: (scrollRef) => {
        this.setState({
          scrollRef,
          scrollHeight: get(scrollRef, "clientHeight", height),
        });
      },
      scrollHeight: height,
    };
  }

  propsToState(pProps) {
    const props = pProps || this.props;
    return {
      transcriptTimeRanges: this.transcriptTimeRanges(props),
      identifiedRanges: props.identifiedRanges,
    };
  }

  componentDidUpdate(newProps, newState) {
    const { transcriptTimeRanges, identifiedRanges } =
      this.propsToState(newProps);
    /**
     * watching transcript values, not scroll height
     */
    if (
      !equal(newState.identifiedRanges, identifiedRanges) ||
      !compareRangeLists(newState.transcriptTimeRanges, transcriptTimeRanges)
    ) {
      this.setState(this.propsToState(newProps));
    }
  }

  getTranscriptContextValue() {
    const transcriptsBySpeaker = this.transcriptBySpeaker();
    const speakers = this.getSpeakersFromTBS(transcriptsBySpeaker);
    const speakerColors = this.getSpeakerColorsFromSpeakers(speakers);
    const transcriptTimeRanges = this.transcriptTimeRanges();
    const identifiedRanges = this.props.identifiedRanges;
    const identifiedRangeBrackets = this.identifiedRangeBrackets();
    const isChatTextMedia = this.props.isChatTextMedia;
    const metadata = this.props.metadata;
    // @TODO: rename, as it is a post-processed version
    return {
      transcriptsBySpeaker,
      speakers,
      speakerColors,
      transcriptTimeRanges,
      identifiedRanges,
      identifiedRangeBrackets,
      isChatTextMedia,
      metadata,
    };
  }

  render() {
    return (
      <TranscriptContext.Provider value={this.getTranscriptContextValue()}>
        <ScrollerContext.Provider value={this.getScrollerContextValue()}>
          <TranscriptView {...this.props} />
        </ScrollerContext.Provider>
      </TranscriptContext.Provider>
    );
  }
}

TranscriptContainer.propTypes = {
  // duration: PropTypes.number.isRequired,
  transcript: PropTypes.arrayOf(
    PropTypes.shape({
      s: PropTypes.number.isRequired,
      e: PropTypes.number.isRequired,
      m: PropTypes.string,
      w: PropTypes.string,
      p: PropTypes.number,
    })
  ).isRequired,
  identifiedRanges: PropTypes.arrayOf(
    PropTypes.shape({
      startTime: PropTypes.number,
      endTime: PropTypes.number,
      startWord: PropTypes.shape({
        p: PropTypes.number,
        w: PropTypes.string,
      }),
      endWord: PropTypes.shape({
        p: PropTypes.number,
        w: PropTypes.string,
      }),
    })
  ),
};

export default TranscriptContainer;
